import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Mic, Volume2, KeyRound, CheckCircle2, Mail } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full h-11 px-3 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";

function StatusPill({ ok, testId }) {
  return ok ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/12 text-emerald-600" data-testid={testId}><CheckCircle2 size={13} /> Connected</span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" data-testid={testId}>Not set</span>
  );
}

export default function AdminSettings() {
  const [el, setEl] = useState(null);
  const [rs, setRs] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [resendSender, setResendSender] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings/integrations");
      setEl(r.data.elevenlabs);
      setVoiceId(r.data.elevenlabs.voice_id || "");
      setRs(r.data.resend || { configured: false });
      setResendSender(r.data.resend?.sender || "");
    } catch (e) { toast.error("Failed to load settings"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { elevenlabs_voice_id: voiceId };
      if (apiKey.trim()) body.elevenlabs_api_key = apiKey.trim();
      await api.put("/admin/settings/integrations", body);
      toast.success("Voice settings saved");
      setApiKey("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const body = { resend_sender: resendSender };
      if (resendKey.trim()) body.resend_api_key = resendKey.trim();
      await api.put("/admin/settings/integrations", body);
      toast.success("Email settings saved");
      setResendKey("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingEmail(false); }
  };

  const testVoice = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/settings/integrations/test-voice", {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      new Audio(url).play();
      toast.success("Playing a sample of the interviewer's voice");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice test failed — check the key");
    } finally { setTesting(false); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-2xl" data-testid="admin-settings">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Mic size={20} className="text-[hsl(var(--accent))]" /> Voice & integrations</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">Configure the platform-wide voice engine and email delivery used across Keepsake Stories.</p>

      {/* ElevenLabs */}
      <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6" data-testid="admin-elevenlabs-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold"><Volume2 size={18} className="text-[hsl(var(--accent))]" /> ElevenLabs (text-to-speech)</div>
          <StatusPill ok={el?.configured} testId="admin-elevenlabs-status" />
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5 flex items-center gap-1.5"><KeyRound size={12} /> API key</label>
            <input data-testid="admin-elevenlabs-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={el?.configured ? `Saved (${el.api_key_masked}) — enter a new key to replace` : "sk_..."} className={inputCls} />
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Find it at elevenlabs.io → Profile → API Keys. Stored securely; never shown again in full.</p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Voice ID <span className="normal-case font-normal">(optional — defaults to a warm narrator)</span></label>
            <input data-testid="admin-elevenlabs-voice-input" value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder="21m00Tcm4TlvDq8ikWAM" className={inputCls} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button data-testid="admin-elevenlabs-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
          {el?.configured && (
            <button data-testid="admin-elevenlabs-test" onClick={testVoice} disabled={testing}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] disabled:opacity-60">
              {testing ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />} Test voice
            </button>
          )}
        </div>
      </div>

      {/* Resend email */}
      <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6" data-testid="admin-resend-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold"><Mail size={18} className="text-[hsl(var(--accent))]" /> Resend (email delivery)</div>
          <StatusPill ok={rs?.configured || rs?.env_fallback} testId="admin-resend-status" />
        </div>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Powers interviewee invitations and family emails. Without a key, invite links can still be copied and shared manually.</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5 flex items-center gap-1.5"><KeyRound size={12} /> API key</label>
            <input data-testid="admin-resend-key-input" type="password" value={resendKey} onChange={(e) => setResendKey(e.target.value)}
              placeholder={rs?.configured ? `Saved (${rs.api_key_masked}) — enter a new key to replace` : "re_..."} className={inputCls} />
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Find it at resend.com → API Keys. Stored securely; never shown again in full.</p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Sender <span className="normal-case font-normal">(optional — a verified from-address)</span></label>
            <input data-testid="admin-resend-sender-input" value={resendSender} onChange={(e) => setResendSender(e.target.value)} placeholder="GatherVoices &lt;hello@yourdomain.com&gt;" className={inputCls} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button data-testid="admin-resend-save" onClick={saveEmail} disabled={savingEmail}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 disabled:opacity-60">
            {savingEmail ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
